// ── Enums ──────

export enum GradeLevel {
  Freshman = 9,
  Sophomore = 10,
  Junior = 11,
  Senior = 12,
}

export enum Term {
  Fall = 'fall',
  Spring = 'spring',
  Summer = 'summer',
  Q1 = 'q1',
  Q2 = 'q2',
  Q3 = 'q3',
  Q4 = 'q4',
  Trimester1 = 't1',
  Trimester2 = 't2',
  Trimester3 = 't3',
}

export enum TermStructure {
  Semester = 'semester',
  Quarter = 'quarter',
  Trimester = 'trimester',
  Block = 'block',
}

export enum CourseType {
  Standard = 'standard',
  Honors = 'honors',
  AP = 'ap',
  IB_SL = 'ib_sl',
  IB_HL = 'ib_hl',
  DualEnrollment = 'dual_enrollment',
  College = 'college',
}

export enum AGSubject {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',
  F = 'F',
  G = 'G',
  None = 'none',
}

export enum GradeFormat {
  Letter = 'letter',
  Percentage = 'percentage',
  Numeric = 'numeric',
  IB_1_7 = 'ib_1_7',
  ECTS = 'ects',
}

export enum GradingScale {
  Scale4 = '4.0',
  Scale5 = '5.0',
  Scale6 = '6.0',
  Scale100 = '100',
  IB1to7 = 'ib_1_7',
}

export enum WeightingPreset {
  Unweighted = 'unweighted',
  UC_Capped = 'uc_capped',
  UC_Uncapped = 'uc_uncapped',
  CommonApp5 = 'common_app_5',
  Custom = 'custom',
}

export enum ResidencyStatus {
  CaliforniaResident = 'ca_resident',
  NonResident = 'non_resident',
}

export enum SpecialGrade {
  Pass = 'P',
  Fail = 'F_passfail',
  Incomplete = 'I',
  Withdrawal = 'W',
  None = '',
}

export enum ValidationSeverity {
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
}

// ── Core Interfaces ──────

export interface Course {
  id: string;
  name: string;
  gradeLevel: GradeLevel;
  term: Term;
  academicYear: string;
  grade: string;
  gradeFormat: GradeFormat;
  credits: number;
  courseType: CourseType;
  agSubject: AGSubject;
  isRepeated: boolean;
  repeatCourseId?: string;
  specialGrade?: SpecialGrade;
  isUCCertifiedHonors?: boolean;
  notes?: string;
}

export interface GradePointMapping {
  grade: string;
  points: number;
  isPassing: boolean;
}

export interface RoundingRule {
  mode: 'none' | 'decimal_1' | 'decimal_2' | 'decimal_3';
  method: 'truncate' | 'round_half_up' | 'round_half_even';
}

export interface CustomWeightingConfig {
  bonusPoints: Partial<Record<CourseType, number>>;
  scaleMaximum: number;
  pointCap?: {
    maxHonorsPoints: number;
    maxPerGradeLevel?: Partial<Record<GradeLevel, number>>;
  };
}

export interface AppSettings {
  gradingScale: GradingScale;
  termStructure: TermStructure;
  plusMinusEnabled: boolean;
  residencyStatus: ResidencyStatus;
  roundingRule: RoundingRule;
  customWeighting?: CustomWeightingConfig;
  gradeScaleMap: GradePointMapping[];
  homeschoolMode?: boolean;
}

// ── Calculation Results ──────

export interface CourseContribution {
  courseId: string;
  gradePoints: number;
  bonusPoints: number;
  totalPoints: number;
  credits: number;
  includedIn: {
    unweighted: boolean;
    ucCapped: boolean;
    ucUncapped: boolean;
    ucUnweighted: boolean;
    commonApp: boolean;
  };
  exclusionReasons: {
    unweighted?: string;
    ucCapped?: string;
    ucUncapped?: string;
    ucUnweighted?: string;
    commonApp?: string;
  };
  explanation: string;
}

export interface GPAVariant {
  label: string;
  value: number;
  includedCourses: number;
  totalCourses: number;
  totalCredits: number;
  isEligible?: boolean;
  threshold?: number;
}

export interface GPABreakdown {
  byTerm: {
    term: Term;
    academicYear: string;
    gpa: Record<string, number>;
  }[];
  cumulative: {
    academicYear: string;
    gpa: Record<string, number>;
  }[];
}

export interface HonorsCapStatus {
  totalCap: number;
  usedPoints: number;
  remainingPoints: number;
  tenthGradeCap: number;
  tenthGradeUsed: number;
  tenthGradeRemaining: number;
}

// ── Validation ──────

export interface ValidationIssue {
  id: string;
  severity: ValidationSeverity;
  courseId?: string;
  field?: string;
  message: string;
  suggestion?: string;
}

// ── Scenario Modeling ──────

export interface ScenarioCourse {
  courseId?: string;
  name: string;
  gradeLevel: GradeLevel;
  term: Term;
  academicYear: string;
  hypotheticalGrade: string;
  courseType: CourseType;
  credits: number;
  agSubject: AGSubject;
}

export interface ScenarioResult {
  currentGPA: Record<string, number>;
  projectedGPA: Record<string, number>;
  delta: Record<string, number>;
}

// ── Persistence ──────

export interface SessionData {
  version: number;
  courses: Course[];
  settings: AppSettings;
  createdAt: string;
  updatedAt: string;
  label?: string;
}

export interface ExportData {
  courses: Course[];
  settings: AppSettings;
  results: GPACalculationResult;
  exportedAt: string;
}

// ── Top-Level Aggregates ──────

export interface GPACalculationResult {
  variants: GPAVariant[];
  contributions: CourseContribution[];
  breakdown: GPABreakdown;
  honorsCap: HonorsCapStatus;
  validationIssues: ValidationIssue[];
  calculatedAt: string;
}

export interface AppState {
  courses: Course[];
  settings: AppSettings;
  result: GPACalculationResult | null;
  isDirty: boolean;
}

// ── Benchmarks (Phase 4) ──────

export interface UCCampusRange {
  name: string;
  code: string;
  gpaRanges: {
    cappedWeighted: { p25: number; p50: number; p75: number };
    uncappedWeighted?: { p25: number; p50: number; p75: number };
  };
  acceptanceRate: number;
}

export interface InstitutionBenchmark {
  name: string;
  gpaRange: { p25: number; p50: number; p75: number };
  scale: '4.0' | '5.0';
  acceptanceRate: number;
  category: string;
}

export interface SchoolContext {
  schoolName: string;
  limitedAP: boolean;
  limitedAPDetails?: string;
  gradingQuirks?: string;
  districtCompetitiveness: 'low' | 'average' | 'high' | 'very_high';
  notes?: string;
}

// ── College Shortlist (Emergent — FE-E5) ──────

export interface ShortlistEntry {
  key: string;
  name: string;
  source: 'uc' | 'commonapp';
  range: { p25: number; p50: number; p75: number };
  scale: string;
}
